import { FC, useMemo } from 'react';
import { ProductData, productDataTransformer } from '@commerce-product-webcomponents/sdk';
import {
  SearchResultCardComponent,
  type SearchResultCardComponentPropsTypes,
} from '@commerce-product-webcomponents/ui';
import { getPrice, Product } from '@commerce-search-webcomponents/sdk';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  TextLink,
  TextLinkStyle,
  Theme,
  useNextgenRouter,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import { ProductsProp } from './types/propTypes';
import styles from './Products.module.scss';

const UPPER_LIMIT_PRODUCTS_SUGGESTIONS = 3;
export const SEARCHED_PRODUCTS_STORAGE_KEY = 'recentSearchedProducts';

const Products: FC<ProductsProp> = ({
  searchText,
  searchTextMinLength,
  isPopularProduct,
  isRecentProduct,
  alternateTerm,
  typeaheadSearchResults,
  productList,
  onClearAllClick,
  onViewAllClick,
  onProductClick,
}) => {
  const t = useTranslation();
  const sessionDetails = useSession();
  const router = useNextgenRouter();
  const sectionTitle = useMemo(
    () =>
      isPopularProduct
        ? t('search.searchOverlay.popularProducts')
        : isRecentProduct
          ? t('search.searchOverlay.searchedProducts')
          : alternateTerm
            ? t('search.searchOverlay.lookingFor').replace('{{keyword}}', `“${alternateTerm}”`)
            : typeaheadSearchResults.total
              ? t('search.searchOverlay.products')
              : '',
    [isPopularProduct, isRecentProduct, typeaheadSearchResults, router.params?.locale],
  );

  const handleProductClick = (
    e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
    productId: string,
    item: Product,
  ) => {
    const recentProducts = (StorageService.getLocalStorage()?.retrieve(SEARCHED_PRODUCTS_STORAGE_KEY) ||
      []) as string[];
    if (!recentProducts.find((id) => id === item.id)) {
      recentProducts.unshift(item.id);
      StorageService.getLocalStorage()?.store(
        SEARCHED_PRODUCTS_STORAGE_KEY,
        JSON.stringify(recentProducts.slice(0, UPPER_LIMIT_PRODUCTS_SUGGESTIONS)),
      );
    }
    if (onProductClick) {
      onProductClick(e, productId);
    }
  };

  return (
    <div className={styles['products-section']}>
      <div className={styles['product-list']} data-testid="products-testId">
        <section className={styles['product-header']}>
          <span className={styles['product-heading']}>{sectionTitle}</span>
          {isRecentProduct && (
            <span className={styles['products-clear']}>
              <TextLink
                text={t('search.common.clearAll')}
                textLinkStyle={TextLinkStyle.UNDERLINE}
                theme={Theme.PURPLE}
                data-testid="clear-all-test-id"
                onClick={(e) => {
                  e.preventDefault();
                  onClearAllClick();
                }}
              />
            </span>
          )}
        </section>
        {productList.map((item: Product) => {
          const productData = {
            ...productDataTransformer(item as ProductData),
            productPrice: getPrice(item, sessionDetails) as number,
          };
          const { flags } = item;
          return (
            <div key={productData.productId}>
              <SearchResultCardComponent
                searchedKeyword={searchText.length >= searchTextMinLength ? searchText : ''}
                disabled={flags.restrictFromAddToCart}
                entryObj={productData as SearchResultCardComponentPropsTypes['entryObj']}
                productData={productData}
                showSubText
                onProductClick={(...args) => handleProductClick(...args, item)}
              />
            </div>
          );
        })}
      </div>
      {!isPopularProduct && !isRecentProduct && productList?.length > 0 && (
        <div className={styles['view-all']}>
          <Button
            title={t('search.searchOverlay.viewAllResults')}
            data-testid="view-all-btn-testId"
            buttonStyle={ButtonStyles.OUTLINE}
            size={ButtonSize.SMALL}
            buttonWidth={ButtonWidth.FULL_WIDTH}
            onClick={onViewAllClick}
          />
        </div>
      )}
    </div>
  );
};

export default Products;
